import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { complianceProgress } from "@/lib/progress";

const barColor = (pct) => (pct === 100 ? "bg-[#16A34A]" : pct >= 50 ? "bg-[#FFD600]" : "bg-[#F59E0B]");

function PartRow({ p }) {
  const pct = p.total ? Math.round((p.done / p.total) * 100) : 0;
  const inner = (
    <>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className="text-sm font-semibold group-hover:text-white transition-colors">{p.label}</span>
        <span className="font-mono-label text-[11px] text-muted-foreground" data-testid={`progress-${p.key}-count`}>{p.done}/{p.total}</span>
      </div>
      <div className="h-1.5 bg-[#262626]"><div className={`h-full ${barColor(pct)} transition-[width] duration-700`} style={{ width: `${pct}%` }} /></div>
      {p.hint && <div className="mt-1 text-[11px] text-[#F59E0B]">{p.hint}</div>}
    </>
  );
  const cls = "group block p-3 -m-3 hover:bg-[#141414] transition-colors";
  return p.to.startsWith("#")
    ? <button type="button" onClick={() => document.getElementById(p.to.slice(1))?.scrollIntoView({ behavior: "smooth" })} className={`${cls} text-left w-[calc(100%+1.5rem)]`} data-testid={`progress-${p.key}`}>{inner}</button>
    : <Link to={p.to} className={cls} data-testid={`progress-${p.key}`}>{inner}</Link>;
}

export default function ComplianceProgress({ profile, records }) {
  const navigate = useNavigate();
  const { parts, done, total, pct } = complianceProgress(profile, records);
  const full = pct === 100;
  const next = parts.find((p) => p.done < p.total);
  return (
    <section className="brutal-border p-5 sm:p-6 mb-10" data-testid="compliance-progress">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
        <div>
          <div className="font-mono-label text-[10px] text-muted-foreground mb-1 inline-flex items-center gap-1.5">
            <ShieldCheck size={12} className={full ? "text-[#16A34A]" : "text-[#FFD600]"} /> COMPLIANCE SETUP
          </div>
          <div className="font-heading font-black text-4xl sm:text-5xl leading-none" data-testid="compliance-progress-pct">
            {pct}%<span className="text-base sm:text-lg font-bold text-muted-foreground ml-2">{full ? "fully compliant" : "complete"}</span>
          </div>
        </div>
        <div className="text-sm text-muted-foreground" data-testid="compliance-progress-count">{done} of {total} required items done</div>
      </div>
      <div className="h-3 bg-[#262626] mb-6" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} data-testid="compliance-progress-bar">
        <div className={`h-full ${barColor(pct)} transition-[width] duration-700`} style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        {parts.map((p) => <PartRow key={p.key} p={p} />)}
      </div>
      {next && (
        <button type="button" onClick={() => (next.to.startsWith("#") ? document.getElementById(next.to.slice(1))?.scrollIntoView({ behavior: "smooth" }) : navigate(next.to))}
          data-testid="compliance-progress-cta" className="mt-6 inline-flex items-center gap-2 font-mono-label text-[11px] text-[#FFD600] hover:underline">
          Next: {next.label} <ArrowRight size={12} />
        </button>
      )}
    </section>
  );
}
